import { useAuth } from '../../../core/auth';
import { useGetAssigmentCount } from '../../../hooks/useAssignmentCount';
import {
  useGetBatchesCountQuery,
  useGetHasAnnotationTasksQuery,
  useGetHasSupervisionBatchesQuery,
  useGetMyProjectsCountQuery,
  useGetTotalProjectsCountQuery,
} from '../api/dashboardApiSlice';

export interface UseAnnotationCardsSectionResult {
  pendingTasksCount: number | null;
  draftTasksCount: number | null;
  finalizedTasksCount: number | null;
  totalTasksCount: number | null;
  myProjectsCount: number | null;
  isProjectsCountLoading: boolean;
  hasSupervisionBatches: boolean;
  isHasSupervisionBatchesLoading: boolean;
  hasAnnotationTasks: boolean;
  isHasAnnotationTasksLoading: boolean;
  isAssignmentCountLoading: boolean;
  // Batch review card — counts and visibility unified for both supervisor and admin
  showBatchReviewCard: boolean;
  batchReviewCount: number | null;
  isBatchReviewCountLoading: boolean;
  isAdminOnly: boolean;
  isAdmin: boolean;
  isUser: boolean;
  isManagerWithoutSupervision: boolean;
}

export const useAnnotationCardsSection = (): UseAnnotationCardsSectionResult => {
  const { user } = useAuth();
  const skipUserQueries = !user.isUser;

  const {
    data: hasSupervisionBatchesData,
    isLoading: isHasSupervisionBatchesLoading,
    isFetching: isHasSupervisionBatchesFetching,
  } = useGetHasSupervisionBatchesQuery(undefined, { skip: skipUserQueries });

  const {
    data: hasAnnotationTasksData,
    isLoading: isHasAnnotationTasksLoading,
    isFetching: isHasAnnotationTasksFetching,
  } = useGetHasAnnotationTasksQuery(undefined, { skip: skipUserQueries });

  const hasSupervisionBatches = hasSupervisionBatchesData?.data?.has ?? false;
  const hasAnnotationTasks = hasAnnotationTasksData?.data?.has ?? false;
  const isAdminOnly = user.isAdmin && !user.isUser;

  // ANNOTATION_MANAGER + ANNOTATION_USER retains Supervisor Workspace access (read-only Review
  // Conflicts card, My Assignments card) whenever there are no active supervision batches,
  // regardless of annotation tasks. No My Projects card, since there are no real supervision
  // batches to browse.
  const isManagerWithoutSupervision = user.isAdmin && user.isUser && !hasSupervisionBatches;

  // Only fetch total-projects count for admin-only users or a manager without supervision
  // (both need the read-only "all projects" count instead of a personal batch count).
  const skipAdminProjectsCount = !(isAdminOnly || isManagerWithoutSupervision);

  const {
    pendingTasksCount: pendingTasksCountData,
    draftTasksCount: draftTasksCountData,
    finalizedTasksCount: finalizedTasksCountData,
    totalTasksCount: totalTasksCountData,
    isLoading: isAssignmentCountLoading,
  } = useGetAssigmentCount(undefined, undefined, skipUserQueries);

  const {
    data: batchesCountData,
    isLoading: isBatchesCountLoading,
    isFetching: isBatchesCountFetching,
  } = useGetBatchesCountQuery(undefined, { skip: skipUserQueries });

  const {
    data: projectsCountData,
    isLoading: isProjectsCountLoading,
    isFetching: isProjectsCountFetching,
  } = useGetMyProjectsCountQuery(undefined, { skip: skipUserQueries });

  // Total projects count used as the batch-review count for admin-only users
  const {
    data: adminTotalProjectsData,
    isLoading: isAdminTotalProjectsLoading,
    isFetching: isAdminTotalProjectsFetching,
  } = useGetTotalProjectsCountQuery(undefined, { skip: skipAdminProjectsCount });

  // Card is shown when the user actually has supervision batches, for an admin-only user (no
  // ANNOTATION_USER role) who has no Annotator Workspace to fall back to, or for a manager
  // with no active work (read-only Review Conflicts).
  const showBatchReviewCard = hasSupervisionBatches || isAdminOnly || isManagerWithoutSupervision;
  const showReadOnlyCount = isAdminOnly || isManagerWithoutSupervision;

  // For admin-only / manager-with-no-active-work: show total projects as the count (all
  // projects they can review, read-only). For an actual supervisor: show their assigned batch count.
  const batchReviewCount = showReadOnlyCount
    ? adminTotalProjectsData?.data?.count ?? null
    : batchesCountData?.data?.count ?? null;

  const isBatchReviewCountLoading = showReadOnlyCount
    ? isAdminTotalProjectsLoading || isAdminTotalProjectsFetching
    : isBatchesCountLoading || isBatchesCountFetching;

  return {
    pendingTasksCount: pendingTasksCountData ?? null,
    draftTasksCount: draftTasksCountData ?? null,
    finalizedTasksCount: finalizedTasksCountData ?? null,
    totalTasksCount: totalTasksCountData ?? null,
    isAssignmentCountLoading,
    myProjectsCount: projectsCountData?.data?.count ?? null,
    isProjectsCountLoading: isProjectsCountLoading || isProjectsCountFetching,
    hasSupervisionBatches,
    isHasSupervisionBatchesLoading:
      isHasSupervisionBatchesLoading || isHasSupervisionBatchesFetching,
    hasAnnotationTasks,
    isHasAnnotationTasksLoading: isHasAnnotationTasksLoading || isHasAnnotationTasksFetching,
    showBatchReviewCard,
    batchReviewCount,
    isBatchReviewCountLoading,
    isAdminOnly,
    isAdmin: user.isAdmin,
    isUser: user.isUser,
    isManagerWithoutSupervision,
  };
};
